import {
  INJURY_BODY_ZONE,
  INJURY_CONTEXT,
  INJURY_MECHANISM,
  INJURY_ONSET,
  INJURY_SEVERITY,
  INJURY_TYPE_EXTENDED,
} from '../enums/injuries';
import { LATERALITY } from '../enums/testing';

export interface IInjuryNote {
  _id: string;
  content: string;
  authorId: string;
  authorName?: string;
  createdAt: string;
}

export interface ILoadSnapshot {
  acwr?: number;
  weeklyLoad?: number;
  monthlyLoad?: number;
  sRPE?: number;
  capturedAt?: string;
}

export interface IInjury {
  _id: string;
  athleteId: string;
  teamId: string;
  bodyZone: INJURY_BODY_ZONE;
  /** @deprecated usa laterality */
  side?: LATERALITY;
  laterality?: LATERALITY;
  injuryDate: string;
  returnDate?: string;
  expectedReturnDate?: string;
  /** @deprecated usa severityEstimated */
  severity?: INJURY_SEVERITY;
  severityEstimated?: INJURY_SEVERITY;
  injuryType: INJURY_TYPE_EXTENDED;
  onset: INJURY_ONSET;
  context?: INJURY_CONTEXT;
  mechanism?: INJURY_MECHANISM;
  description?: string;
  diagnosis?: string;
  treatingPhysio?: string;
  isActive: boolean;
  isRecurrence?: boolean;
  rtpProtocolId?: string;
  /** ID del documento BodyMapComplaint che ha originato questo infortunio */
  sourceBodyMapComplaintId?: string;
  loadSnapshot?: ILoadSnapshot;
  notes: IInjuryNote[];
  createdAt: string;
  updatedAt: string;
}

export interface IActiveInjurySummary {
  injuryId: string;
  athleteId: string;
  bodyZone: INJURY_BODY_ZONE;
  daysOut: number;
  severity: INJURY_SEVERITY;
  rtpPhase?: number;
  expectedReturnDate?: string;
}

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface ICreateInjuryDto {
  athleteId: string;
  teamId: string;
  bodyZone: INJURY_BODY_ZONE;
  laterality?: LATERALITY;
  injuryDate: string;
  severityEstimated: INJURY_SEVERITY;
  injuryType: INJURY_TYPE_EXTENDED;
  onset: INJURY_ONSET;
  context?: INJURY_CONTEXT;
  mechanism?: INJURY_MECHANISM;
  description?: string;
  diagnosis?: string;
  treatingPhysio?: string;
  expectedReturnDate?: string;
  createRtpProtocol?: boolean;
  /** Dati protocollo RTP — NESTED come atteso dal backend (RtpProtocolDataDto). */
  rtpProtocolData?: {
    injuryType: string;
    injurySide: string;
    enabledTests?: string[];
    notes?: string;
  };
  /** ID del documento BodyMapComplaint che ha originato questo infortunio (Fase 4) */
  sourceBodyMapComplaintId?: string;
}

export interface IUpdateInjuryDto {
  bodyZone?: INJURY_BODY_ZONE;
  laterality?: LATERALITY;
  injuryDate?: string;
  severityEstimated?: INJURY_SEVERITY;
  injuryType?: INJURY_TYPE_EXTENDED;
  onset?: INJURY_ONSET;
  context?: INJURY_CONTEXT;
  mechanism?: INJURY_MECHANISM;
  description?: string;
  diagnosis?: string;
  treatingPhysio?: string;
  expectedReturnDate?: string;
}

export interface IRegisterReturnDto {
  returnDate: string;
  notes?: string;
}

export interface IInjuryNoteDto {
  content: string;
}

// ─── Metric response types ────────────────────────────────────────────────────

export interface IInjuryMarker {
  injuryId: string;
  date: string;
  bodyZone: string;
  severity: string;
}

export interface ITimeSeries {
  date: string;
  value: number;
}

// timeline
export interface ITimelineMetric {
  series: ITimeSeries[];
  injuryMarkers: IInjuryMarker[];
}

// pain-nrs
export interface IPainNrsMetric {
  series: Array<{ date: string; nrs: number; injuryId: string }>;
}

// body-map: giorni persi per zona corporea
export interface IBodyMapMetric {
  zones: Array<{ zone: string; daysLost: number; count: number }>;
}

// recurrence-map
export interface IRecurrenceMapMetric {
  zones: Array<{ zone: string; recurrenceCount: number; totalCount: number }>;
}

// load-at-injury
export interface ILoadAtInjuryMetric {
  events: Array<{ injuryId: string; date: string; acwr: number; weeklyLoad: number; baselineAcwr: number }>;
}

// return-confidence
export interface IReturnConfidenceMetric {
  series: Array<{ date: string; confidence: number; fear: number }>;
}

// days-lost-severity
export interface IDaysLostSeverityMetric {
  buckets: Array<{ severity: string; totalDays: number; avgDays: number; count: number }>;
}

// type-breakdown
export interface ITypeBreakdownMetric {
  items: Array<{ type: string; count: number; pct: number }>;
}

// context-breakdown
export interface IContextBreakdownMetric {
  items: Array<{ context: string; count: number; pct: number }>;
}

// monthly-rate
export interface IMonthlyRateMetric {
  months: Array<{ month: string; count: number; rate: number }>;
}

// rtp-progress
export interface IRtpProgressMetric {
  protocolId: string;
  phase: number;
  totalPhases: number;
  pct: number;
  injuryType: string;
}

// reinjury-risk
export interface IReinjuryRiskMetric {
  score: number; // 0-100
  level: 'low' | 'moderate' | 'high' | 'very-high';
  factors: Array<{ label: string; contribution: number }>;
}

// mechanism-breakdown
export interface IMechanismBreakdownMetric {
  items: Array<{ mechanism: string; count: number; pct: number }>;
}

// acwr-series
export interface IAcwrSeriesMetric {
  series: Array<{ date: string; acwr: number; acute: number; chronic: number }>;
  injuryMarkers: IInjuryMarker[];
}

// srpe-weekly
export interface ISrpeWeeklyMetric {
  weeks: Array<{ week: string; totalSrpe: number; sessions: number }>;
  injuryMarkers: IInjuryMarker[];
}

// readiness-index
export interface IReadinessIndexMetric {
  series: ITimeSeries[];
  injuryMarkers: IInjuryMarker[];
}

// hooper-series: 4 dimensioni
export interface IHooperSeriesMetric {
  series: Array<{ date: string; fatigue: number; sleepQuality: number; muscleSoreness: number; stressLevel: number }>;
  injuryMarkers: IInjuryMarker[];
}

// tonnage-series
export interface ITonnageSeriesMetric {
  weeks: Array<{ week: string; tonnage: number }>;
  injuryMarkers: IInjuryMarker[];
}

// exposure-hours
export interface IExposureHoursMetric {
  months: Array<{ month: string; hours: number; injuryCount: number }>;
  injuryMarkers: IInjuryMarker[];
}

// cmj-rsi-series
export interface ICmjRsiSeriesMetric {
  series: Array<{ date: string; cmj: number; rsi: number }>;
  benchmarks: { cmj?: number; rsi?: number };
  injuryMarkers: IInjuryMarker[];
}

// hrv-series
export interface IHrvSeriesMetric {
  series: ITimeSeries[];
  injuryMarkers: IInjuryMarker[];
}

// load-progression: per un esercizio
export interface ILoadProgressionMetric {
  exercise: string;
  series: Array<{ date: string; load: number; rpe: number }>;
}

// insights
export interface IInsightItem {
  key: string;
  label: string;
  type: 'positive' | 'warning' | 'negative' | 'neutral';
  value?: number;
  baseline?: number;
  deltaPct?: number;
}

export interface IInsightsMetric {
  injuryId?: string;
  period?: string;
  insights: IInsightItem[];
  meta?: {
    dataQuality?: string;
    dataPoints?: number;
  };
}

// capabilities: quali metriche sono disponibili per l'atleta
// Il backend restituisce { [metricSlug]: boolean } (CapabilitiesMap)
export type IAthleteCapabilities = Record<string, boolean>;

// ─── Team metric types (Phase 5) ─────────────────────────────────────────────

export type ITeamCapabilities = Record<string, boolean>;

export interface ITeamTimelineMetric {
  period: string;
  data: Array<{
    injuryId: string;
    athleteId: string;
    firstName: string;
    lastName: string;
    date: string;
    bodyZone: string;
    injuryType: string;
    status: string;
    daysLost: number | null;
    isRecurrence: boolean;
    acwrAtInjury: number | null;
    riAtInjury: number | null;
  }>;
  meta: Record<string, unknown>;
}

export interface ITeamBodyMapMetric {
  period: string;
  data: Array<{
    bodyZone: string;
    count: number;
    totalDaysLost: number;
    recurrenceCount: number;
    lastInjuryDate: string | null;
  }>;
  meta: Record<string, unknown>;
}

export interface ITeamTypeBreakdownMetric {
  period: string;
  data: Array<{ type: string; count: number; pct: number }>;
  meta: Record<string, unknown>;
}

export interface ITeamMechanismMetric {
  items: Array<{ mechanism: string; count: number; pct: number }>;
}

export interface ITeamSeverityMetric {
  period: string;
  data: Array<{ severity: string; count: number; totalDaysLost: number }>;
  meta: Record<string, unknown>;
}

export interface ITeamLeaderboardMetric {
  period: string;
  data: Array<{
    athleteId: string;
    firstName: string;
    lastName: string;
    totalInjuries: number;
    totalDaysLost: number;
    activeInjuries: number;
    recurrenceCount: number;
  }>;
  meta: Record<string, unknown>;
}

export interface ITeamRecurrenceMetric {
  period: string;
  data: Array<{
    position: string | null;
    totalInjuries: number;
    recurrences: number;
    recurrenceRate: number;
  }>;
  meta: Record<string, unknown>;
}

export interface ITeamMonthlyRateMetric {
  period: string;
  data: Array<{ month: string; count: number; rate: number }>;
  meta: Record<string, unknown>;
}

export interface ITeamReturnConfidenceMetric {
  period: string;
  data: Array<{
    avgConfidence: number | null;
    activeInjuriesCount: number;
    athletesWithData: number;
  }>;
  meta: Record<string, unknown>;
}

export interface ITeamRtpStatusMetric {
  period: string;
  data: Array<{ phase: string; count: number }>;
  meta: Record<string, unknown>;
}

export interface ITeamAcwrMetric {
  period: string;
  series: Array<{ date: string; acwr: number; acute: number; chronic: number }>;
  injuryMarkers: IInjuryMarker[];
  meta: Record<string, unknown>;
}

export interface ITeamHooperSeriesMetric {
  period: string;
  series: Array<{
    date: string;
    fatigue: number | null;
    sleepQuality: number | null;
    muscleSoreness: number | null;
    stressLevel: number | null;
    overall: number | null;
  }>;
  injuryMarkers: IInjuryMarker[];
  meta: Record<string, unknown>;
}

export interface ITeamReadinessIndexMetric {
  period: string;
  series: Array<{ date: string; value: number | null }>;
  injuryMarkers: IInjuryMarker[];
  meta: Record<string, unknown>;
}

export interface ITeamCmjRsiSeriesMetric {
  period: string;
  series: Array<{ date: string; cmj: number | null; rsi: number | null }>;
  benchmarks?: { cmj?: number; rsi?: number };
  injuryMarkers: IInjuryMarker[];
  meta: Record<string, unknown>;
}

// ─── Certificate status ───────────────────────────────────────────────────────

export interface ICertificateStatus {
  medicalCertificateExpiry?: string; // ISO date string, può essere undefined
}

export type CertificateState = 'valid' | 'expiring-soon' | 'expired' | 'missing';

export function getCertificateState(expiry?: string): CertificateState {
  if (!expiry) return 'missing';
  const days = Math.ceil((new Date(expiry).getTime() - Date.now()) / 86400000);
  if (days < 0) return 'expired';
  if (days <= 30) return 'expiring-soon';
  return 'valid';
}

export function getCertificateDaysLeft(expiry: string): number {
  return Math.ceil((new Date(expiry).getTime() - Date.now()) / 86400000);
}
